import { UserLocaleEnum } from 'collections/users';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAiCopilotTranslateMutation } from 'utils/graphql/mutations/__generated__/aiCopilotTranslate';
import { useAiCopilotTranslationStatusQuery } from 'utils/graphql/queries/__generated__/aiCopilotTranslationStatus';

export enum TranslateRequest {
  SHOW_TRANSLATION = 'show_translation',
  REWRITE = 'rewrite',
  REFRESH_PROOF_READING = 'refresh_proof_reading',
}

interface TranslateArgs {
  fieldName: string;
  targetId: string;
  targetType: string;
  text: string;
  targetLanguage?: string;
  instructions?: string;
  proofReadingLanguage?: string;
  translateRequest?: TranslateRequest;
  translation?: string;
}

interface UseAiTranslatorParams {
  defaultTargetLanguage?: string;
  pollIntervalMs?: number;
  maxWaitMs?: number;
  cache?: Map<string, string>;
  minTextLength?: number;
}

const DEFAULT_TARGET_LANGUAGE = UserLocaleEnum.En;
const DEFAULT_POLL_INTERVAL_MS = 800;
const DEFAULT_MAX_WAIT_MS = 20000;

enum TranslationStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
  PENDING = 'pending',
}

const makeCacheKey = (text: string, lang: string) => `${lang}_${text}`;

export const useAiTranslator = ({
  defaultTargetLanguage = DEFAULT_TARGET_LANGUAGE,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  maxWaitMs = DEFAULT_MAX_WAIT_MS,
  cache,
  minTextLength = 1,
}: UseAiTranslatorParams) => {
  // Final translated/improved message
  const [finalTranslation, setFinalTranslation] = useState<string | null>(null);

  // Proof-reading result (grammar/meaning explanation)
  const [proofReading, setProofReading] = useState<string | null>(null);

  // ID returned by backend used to poll translation status
  const [responseId, setResponseId] = useState<string | null>(null);

  const [error, setError] = useState<Error | null>(null);

  const timeoutRef = useRef<number | null>(null);

  const [translateMutation, { loading: mutationLoading }] = useAiCopilotTranslateMutation();

  const {
    data: statusData,
    error: statusError,
    loading: statusLoading,
    startPolling,
    stopPolling,
  } = useAiCopilotTranslationStatusQuery({
    variables: responseId ? { responseId } : undefined,
    skip: !responseId,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  /**
   * Handle results of polling:
   * - COMPLETED - store translation/proofReading + stop polling
   * - FAILED - set error + stop polling
   */
  useEffect(() => {
    const payload = statusData?.aiCopilotTranslationStatus;

    if (!payload) return;

    if (payload.status === TranslationStatus.COMPLETED) {
      stopPolling();
      clearTimeout(timeoutRef.current ?? undefined);
      timeoutRef.current = null;

      setFinalTranslation(payload.translation ?? '');
      setProofReading(payload.proofReading ?? null);
      if (!payload.translation && !payload.proofReading) {
        setError(new Error('Failed to translate'));
      }

      return;
    }

    if (payload.status === TranslationStatus.FAILED) {
      stopPolling();
      clearTimeout(timeoutRef.current ?? undefined);
      timeoutRef.current = null;
      setError(new Error(`Translation ${payload.status}`));
    }
  }, [statusData, stopPolling]);

  /**
   * When responseId changes:
   * - Start polling for translation/proof-reading status
   * - Start a global timeout
   * - Cleanup both when:
   *    - unmounting
   *    - responseId changes
   *    - polling finishes
   */
  useEffect(() => {
    if (!responseId) return;

    startPolling(pollIntervalMs);

    clearTimeout(timeoutRef.current ?? undefined);
    timeoutRef.current = setTimeout(() => {
      stopPolling();
      setError(new Error('Translation failed to complete. Please try again'));
    }, maxWaitMs) as unknown as number;

    return () => {
      stopPolling();
      clearTimeout(timeoutRef.current ?? undefined);
      timeoutRef.current = null;
    };
  }, [responseId, startPolling, stopPolling, pollIntervalMs, maxWaitMs]);

  const handleTranslateMessage = useCallback(
    async (args: TranslateArgs) => {
      setError(null);
      setFinalTranslation(null);
      setProofReading(null);
      setResponseId(null);

      const text = (args.text ?? '').trim();

      if (text.length < minTextLength) {
        setFinalTranslation('');
        setProofReading(null);

        return;
      }

      const lang = args.targetLanguage ?? defaultTargetLanguage;

      const shouldTryCache =
        (args.translateRequest ?? TranslateRequest.SHOW_TRANSLATION) !== TranslateRequest.REFRESH_PROOF_READING;

      if (cache && shouldTryCache) {
        const key = makeCacheKey(text, lang);
        const cachedTranslation = cache.get(key);

        if (cachedTranslation) {
          setFinalTranslation(cachedTranslation);

          return;
        }
      }

      const { data } = await translateMutation({
        variables: {
          args: {
            fieldName: args.fieldName,
            targetId: args.targetId,
            targetType: args.targetType,
            text,
            targetLanguage: lang,
            instructions: args.instructions,
            proofReadingLanguage: args.proofReadingLanguage,
            translateRequest: args.translateRequest,
            translation: args.translation,
          },
        },
      });

      const payload = data?.aiCopilotTranslate;

      if (!payload?.responseId) {
        setError(new Error('No responseId from server'));

        return;
      }

      setResponseId(payload.responseId);

      if (payload.status === TranslationStatus.COMPLETED) {
        stopPolling();
        clearTimeout(timeoutRef.current ?? undefined);
        timeoutRef.current = null;

        const translation = payload.translation ?? '';
        setFinalTranslation(translation);
        if (cache && shouldTryCache) cache.set(makeCacheKey(text, lang), translation);
      }
    },
    [defaultTargetLanguage, cache, translateMutation, stopPolling, minTextLength],
  );

  useEffect(() => {
    if (statusError) {
      stopPolling();
      clearTimeout(timeoutRef.current ?? undefined);
      timeoutRef.current = null;
      setError(statusError);
    }
  }, [statusError, stopPolling]);

  const cancel = useCallback(() => {
    setResponseId(null);
    stopPolling();
    clearTimeout(timeoutRef.current ?? undefined);
    timeoutRef.current = null;
  }, [stopPolling]);

  const reset = useCallback(() => {
    cancel();
    setFinalTranslation(null);
    setProofReading(null);
    setError(null);
  }, [cancel]);

  const isPending = (!!responseId && !error && !finalTranslation && !proofReading) || mutationLoading || statusLoading;

  return {
    finalTranslation,
    proofReading,
    handleTranslateMessage,
    isPending,
    error,
    responseId,
    cancel,
    reset,
  };
};
